// super-excalidraw: full-screen presentation player with Magic-Move morphing.
//
// Each frame is a slide. Rather than panning the live canvas (a hard cut), the
// player renders a deterministic "stage": every slide is letterboxed into the
// viewport and drawn from per-element SVG snapshots. On slide change, elements
// that share a `customData.morphId` across the two frames CSS-transition from
// their slide-A rect to their slide-B rect (position / size / angle / opacity);
// unmatched elements crossfade. Set up identity with Ctrl+Shift+D ("duplicate
// into next frame") in the editor.
//
// Arrow keys / Page Up-Down / Space navigate; Home/End jump; ESC ends.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { slideItems, type StageItem } from "../data/morph";

import "./PresentationPlayer.scss";

export type PresentationPlayerProps = {
  excalidrawAPI: ExcalidrawImperativeAPI;
  /** Frame ids in playback order (already filtered to live frames). */
  slides: string[];
  onEnd: () => void;
};

const MORPH_MS = 650;

export const PresentationPlayer: React.FC<PresentationPlayerProps> = ({
  excalidrawAPI,
  slides,
  onEnd,
}) => {
  const [cur, setCur] = useState(0);
  const [prev, setPrev] = useState<number | null>(null); // transitioning-from
  const [entering, setEntering] = useState(false);
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scene snapshot (presentation is a read-only view; capture once).
  const elements = useMemo(
    () => excalidrawAPI.getSceneElements() as any[],
    [excalidrawAPI],
  );
  const bg = useMemo(
    () => (excalidrawAPI.getAppState() as any).viewBackgroundColor || "#ffffff",
    [excalidrawAPI],
  );

  // Per-element SVG data URLs (rendered once via exportToSvg, angle zeroed so we
  // can rotate via CSS and tween it). null until ready.
  const [svgMap, setSvgMap] = useState<Record<string, string> | null>(null);
  const [exportFailed, setExportFailed] = useState(false);

  // Stage size tracks the viewport.
  const [stage, setStage] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => {
    const onResize = () => setStage({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Build the SVG snapshots for every element that belongs to any slide.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let exportToSvg: any;
      try {
        const mod: any = await import("@excalidraw/utils/export");
        exportToSvg = mod.exportToSvg;
      } catch {
        if (!cancelled) setExportFailed(true);
        return;
      }
      if (!exportToSvg) {
        if (!cancelled) setExportFailed(true);
        return;
      }
      const files = excalidrawAPI.getFiles();
      const wanted = new Set<string>();
      for (const fid of slides) {
        for (const el of elements) {
          if (!el.isDeleted && el.type !== "frame" && el.frameId === fid) {
            wanted.add(el.id);
          }
        }
      }
      const byId = new Map(elements.map((e) => [e.id, e]));
      const out: Record<string, string> = {};
      for (const id of wanted) {
        if (cancelled) return;
        const el = byId.get(id);
        if (!el) continue;
        try {
          const svg: SVGSVGElement = await exportToSvg({
            // angle zeroed → CSS handles (and tweens) rotation.
            elements: [{ ...el, angle: 0 }],
            appState: { exportBackground: false, viewBackgroundColor: "transparent" },
            files,
            exportPadding: 0,
          });
          svg.setAttribute("width", "100%");
          svg.setAttribute("height", "100%");
          svg.setAttribute("preserveAspectRatio", "none");
          const str = new XMLSerializer().serializeToString(svg);
          out[id] = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(str)));
        } catch {
          // skip this element's snapshot
        }
      }
      if (!cancelled) setSvgMap(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [elements, slides, excalidrawAPI]);

  // Enter zen + view mode on the canvas underneath (the stage covers it, but
  // this also suppresses canvas keyboard handling). Restore on unmount.
  useEffect(() => {
    const before = excalidrawAPI.getAppState();
    const priorZen = !!before.zenModeEnabled;
    const priorView = !!before.viewModeEnabled;
    excalidrawAPI.updateScene({
      appState: { ...before, zenModeEnabled: true, viewModeEnabled: true } as any,
    });
    return () => {
      const c = excalidrawAPI.getAppState();
      excalidrawAPI.updateScene({
        appState: { ...c, zenModeEnabled: priorZen, viewModeEnabled: priorView } as any,
      });
    };
  }, [excalidrawAPI]);

  // Navigate to a slide, kicking off a morph transition from the current one.
  const goTo = (next: number) => {
    const clamped = Math.max(0, Math.min(slides.length - 1, next));
    setCur((c) => {
      if (clamped === c) return c;
      setPrev(c);
      setEntering(false);
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
      transitionTimer.current = setTimeout(() => {
        setPrev(null);
        setEntering(false);
      }, MORPH_MS);
      return clamped;
    });
  };

  // After a transition mounts (prev set, entering=false), force a reflow then
  // flip entering=true so the CSS transitions run A→B.
  useLayoutEffect(() => {
    if (prev === null) return;
    // Reading offsetWidth forces a synchronous reflow so the next style change
    // is a transition, not the initial paint.
    void document.body.offsetWidth;
    const raf = requestAnimationFrame(() => setEntering(true));
    return () => cancelAnimationFrame(raf);
  }, [prev, cur]);

  // Keyboard navigation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
        case "PageDown":
        case " ":
        case "Enter":
          e.preventDefault();
          goTo(cur + 1);
          break;
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          goTo(cur - 1);
          break;
        case "Home":
          e.preventDefault();
          goTo(0);
          break;
        case "End":
          e.preventDefault();
          goTo(slides.length - 1);
          break;
        case "Escape":
          e.preventDefault();
          onEnd();
          break;
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur, slides.length, onEnd]);

  // Fullscreen on mount.
  useEffect(() => {
    const root = document.documentElement;
    if (root.requestFullscreen) root.requestFullscreen().catch(() => {});
    return () => {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  useEffect(() => () => {
    if (transitionTimer.current) clearTimeout(transitionTimer.current);
  }, []);

  // ── Build the renderable node list for the current render ──────────────────
  // At rest: just the current slide. Mid-transition: the union of prev+cur,
  // keyed so morph-matched nodes persist and CSS-transition between rects.
  // morph = a morphId element (glides via CSS transition). enter = entrance
  // style for a non-morph element (plays a CSS keyframe on mount). On slide
  // change, matched elements morph and new elements animate in — live, on canvas.
  type Node = {
    key: string; src: string; r: StageItem["rect"]; opacity: number;
    morph: boolean; enter: string | null;
  };

  const nodes: Node[] = useMemo(() => {
    if (!svgMap) return [];
    const mk = (
      it: StageItem, rect: StageItem["rect"], opacity: number,
      morph: boolean, enter: string | null,
    ): Node | null => {
      const src = svgMap[it.elementId];
      if (!src) return null;
      return { key: it.key, src, r: rect, opacity, morph, enter };
    };

    if (prev === null) {
      // At rest: morph elements sit at their rect; non-morph elements carry an
      // enter class so the CSS animation plays once when they mount.
      return slideItems(elements, slides[cur], stage.w, stage.h)
        .map((it) => mk(it, it.rect, it.opacity, !!it.morphId, it.morphId ? null : it.enter))
        .filter(Boolean) as Node[];
    }

    const a = slideItems(elements, slides[prev], stage.w, stage.h);
    const b = slideItems(elements, slides[cur], stage.w, stage.h);
    const aByKey = new Map(a.map((it) => [it.key, it]));
    const bByKey = new Map(b.map((it) => [it.key, it]));
    const keys = new Set<string>([...aByKey.keys(), ...bByKey.keys()]);
    const list: Node[] = [];
    for (const key of keys) {
      const ai = aByKey.get(key);
      const bi = bByKey.get(key);
      if (ai && bi) {
        // Matched morphId — glide A→B (CSS transition).
        const n = mk(bi, entering ? bi.rect : ai.rect, entering ? bi.opacity : ai.opacity, true, null);
        if (n) list.push(n);
      } else if (ai && !bi) {
        // Leaving — fade out in place.
        const n = mk(ai, ai.rect, entering ? 0 : ai.opacity, true, null);
        if (n) list.push(n);
      } else if (bi) {
        // Entering. Non-morph element: render at final rect and let its enter
        // keyframe play on mount. Morph element's first appearance: fade in.
        const isMorph = !!bi.morphId;
        const n = isMorph
          ? mk(bi, bi.rect, entering ? bi.opacity : 0, true, null)
          : mk(bi, bi.rect, bi.opacity, false, bi.enter);
        if (n) list.push(n);
      }
    }
    return list;
  }, [svgMap, elements, slides, cur, prev, entering, stage.w, stage.h]);

  if (slides.length === 0) return null;

  const atFirst = cur === 0;
  const atLast = cur === slides.length - 1;
  const transitioning = prev !== null;

  return (
    <div className="presentation-player" style={{ background: bg }}>
      <div className="presentation-player__stage">
        {svgMap === null && !exportFailed && (
          <div className="presentation-player__msg">Preparing slides…</div>
        )}
        {exportFailed && (
          <div className="presentation-player__msg">
            Couldn't render slides in this build.
          </div>
        )}
        {svgMap &&
          nodes.map((n) => {
            const cls = ["presentation-player__el"];
            if (n.morph && transitioning) cls.push("presentation-player__el--anim");
            if (n.enter) cls.push(`presentation-player__el--enter-${n.enter}`);
            return (
              <img
                key={n.key}
                src={n.src}
                alt=""
                className={cls.join(" ")}
                draggable={false}
                style={{
                  left: n.r.left,
                  top: n.r.top,
                  width: n.r.width,
                  height: n.r.height,
                  opacity: n.opacity,
                  transform: n.r.angle ? `rotate(${n.r.angle}rad)` : undefined,
                }}
              />
            );
          })}
      </div>

      <div className="presentation-player__controls">
        <button
          type="button"
          className="presentation-player__btn"
          onClick={() => goTo(cur - 1)}
          disabled={atFirst}
          title="Previous (←)"
          aria-label="Previous slide"
        >
          ←
        </button>
        <span className="presentation-player__counter" aria-live="polite">
          Slide {cur + 1}/{slides.length}
        </span>
        <button
          type="button"
          className="presentation-player__btn"
          onClick={() => goTo(cur + 1)}
          disabled={atLast}
          title="Next (→)"
          aria-label="Next slide"
        >
          →
        </button>
        <span className="presentation-player__sep" aria-hidden="true" />
        <button
          type="button"
          className="presentation-player__btn"
          onClick={() => {
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(() => {});
            } else {
              document.documentElement.requestFullscreen().catch(() => {});
            }
          }}
          title="Toggle fullscreen"
          aria-label="Toggle fullscreen"
        >
          ⛶
        </button>
        <button
          type="button"
          className="presentation-player__end"
          onClick={onEnd}
          title="End presentation (Esc)"
        >
          End presentation
        </button>
      </div>
    </div>
  );
};
